import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "hidden sm:block" }
const _hoisted_2 = {
  autocomplete: "off",
  class: "flex flex-col gap-3"
}
const _hoisted_3 = { class: "grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6" }
const _hoisted_4 = {
  key: 0,
  class: "space-y-2 sm:col-span-4"
}
const _hoisted_5 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_6 = {
  key: 1,
  class: "space-y-2 sm:col-span-4"
}
const _hoisted_7 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_8 = { class: "space-y-2 sm:col-span-4" }
const _hoisted_9 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_10 = { class: "space-y-2 sm:col-span-4" }
const _hoisted_11 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_12 = { class: "flex flex-col gap-0.5 py-1" }
const _hoisted_13 = { class: "flex flex-row gap-3" }
const _hoisted_14 = { class: "text-sm text-gray-600" }
const _hoisted_15 = { class: "space-y-2 sm:col-span-4" }
const _hoisted_16 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_17 = { class: "space-y-2 sm:col-span-4" }
const _hoisted_18 = { class: "block text-left text-sm font-medium text-gray-800" }
const _hoisted_19 = { class: "flex rounded-md shadow-sm dark:shadow-none" }
const _hoisted_20 = { class: "space-y-2 sm:col-span-4" }
const _hoisted_21 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_22 = { class: "mt-4 grid grid-cols-2 place-content-around gap-4 text-center" }

import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useFilterStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import { SelectOptionsService } from '@/services';

import { FilterIcon } from '@heroicons/vue/solid';

import {
    CallActions,
    CallFilter,
    CallGetters,
    CallParticipantType,
    CallStatus,
    CallType,
    LevelModel,
    SelectOptionsServiceGetContactPhoneNumbersResult,
    SelectOptionsServiceGetMediasResult,
    SelectOptionsServiceGetUserLabelsResult,
    SelectOptionsServiceGetUsersResult,
    SelectOptionsServiceGetWordsResult,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'CallListFilters',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const filterStore = useFilterStore();

const filtersOpened = ref(false);
const selectedParticipant = ref<CallParticipantType>(CallParticipantType.All);
const filter: Ref<CallFilter> = ref({
    type: null,
    status: null,
    userLabel: null,
    media: null,
    numberClient: null,
    user: null,
    word: null,
});

const wordsOptions: Ref<SelectOptionsServiceGetWordsResult[]> = ref([]);
const userLabelOptions: Ref<SelectOptionsServiceGetUserLabelsResult[]> = ref([]);
const agentOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);
const mediaFilterOptions: Ref<SelectOptionsServiceGetMediasResult[]> = ref([]);
const clientNumberOptions: Ref<SelectOptionsServiceGetContactPhoneNumbersResult[]> = ref([]);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const storeFilters = computed<CallFilter>(() => store.getters[CallGetters.FILTER]);

const isCallTypeRoute = computed(() => {
    return Object.values(CallType)
        .map((value) => value.toLowerCase())
        .some((callTypeValue) => route.path.toLowerCase().includes(callTypeValue));
});

const isCallStatusRoute = computed(() => {
    return Object.values(CallStatus)
        .map((value) => value.toLowerCase())
        .some((callStatusValue) => route.path.toLowerCase().includes(callStatusValue));
});

const isLoadingWords = ref(false);

const statusFilterOptions = [
    { label: t(`call.status.${CallStatus.ANSWERED}`), value: CallStatus.ANSWERED },
    { label: t(`call.status.${CallStatus.BUSY}`), value: CallStatus.BUSY },
    { label: t(`call.status.${CallStatus.NO_ANSWER}`), value: CallStatus.NO_ANSWER },
    { label: t(`call.status.${CallStatus.DITCHED}`), value: CallStatus.DITCHED },
];

const typeFilterOptions = [
    { label: t(`call.type.${CallType.INBOUND}`), value: CallType.INBOUND },
    { label: t(`call.type.${CallType.OUTBOUND}`), value: CallType.OUTBOUND },
    { label: t(`call.type.${CallType.INTERNAL}`), value: CallType.INTERNAL },
];

const callParticipantOptions = [
    { label: t('word.participant.ALL'), value: CallParticipantType.All },
    { label: t('word.participant.AGENT'), value: CallParticipantType.Agent },
    { label: t('word.participant.CLIENT'), value: CallParticipantType.Client },
];

const refreshLocalFilter = () => {
    filter.value.type = storeFilters.value.type;
    filter.value.status = storeFilters.value.status;
    filter.value.userLabel = storeFilters.value.userLabel;
    filter.value.media = storeFilters.value.media;
    filter.value.numberClient = storeFilters.value.numberClient;
    filter.value.user = storeFilters.value.user;
    filter.value.word = storeFilters.value.word;
};

const applyFilters = () => {
    store.dispatch(CallActions.SET_FILTER, filter.value);
};

const clearFilters = () => {
    store.dispatch(CallActions.CLEAR_FILTER);
    refreshLocalFilter();
};

const fetchWords = async () => {
    try {
        isLoadingWords.value = true;
        const response = await SelectOptionsService.getWords({
            params: {
                call_participant: selectedParticipant.value,
            },
        });
        wordsOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
        wordsOptions.value = [];
    } finally {
        isLoadingWords.value = false;
    }
};

const fetchUserLabels = async () => {
    try {
        const response = await SelectOptionsService.getUserLabels();
        userLabelOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchAgents = async () => {
    try {
        const response = await SelectOptionsService.getUsers();
        agentOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchMedias = async () => {
    try {
        const response = await SelectOptionsService.getMedias();
        mediaFilterOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
        mediaFilterOptions.value = [];
    }
};

const fetchClientNumbers = async () => {
    const { data } = await SelectOptionsService.getContactPhoneNumbers();
    clientNumberOptions.value = data;
};

const filterCallParticipantTypes = (value: CallParticipantType) => {
    selectedParticipant.value = value || CallParticipantType.All;
    filter.value.word = null;
};

const getButtonVariant = (participantType: CallParticipantType) => {
    if (selectedParticipant.value != participantType) {
        return 'gray-100';
    }

    switch (participantType) {
        case CallParticipantType.Agent:
            return 'purple-200';
        case CallParticipantType.Client:
            return 'blue-200';
        default:
            return 'gray-200';
    }
};

const getTextVariant = (participantType: CallParticipantType) => {
    switch (participantType) {
        case CallParticipantType.Agent:
            return 'purple-700';
        case CallParticipantType.Client:
            return 'blue-700';
        default:
            return 'gray-700';
    }
};

onMounted(() => {
    fetchUserLabels();
    fetchAgents();
    fetchWords();
    fetchClientNumbers();
    fetchMedias();
});

watch(filtersOpened, () => {
    refreshLocalFilter();
});

watch(levelFilter, () => {
    store.dispatch(CallActions.CLEAR_FILTER);
    fetchUserLabels();
    fetchAgents();
    fetchWords();
    fetchClientNumbers();
    fetchMedias();
});

watch(selectedParticipant, () => {
    fetchWords();
});

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiRightPanel = _resolveComponent("UiRightPanel")!

  return (_openBlock(), _createBlock(_component_UiRightPanel, {
    modelValue: filtersOpened.value,
    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((filtersOpened).value = $event)),
    title: _ctx.$t('filter.sidebar.title')
  }, {
    button: _withCtx(() => [
      _createVNode(_component_UiButton, {
        size: "md",
        variant: "white",
        "text-variant": "gray-700",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (filtersOpened.value = true))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(FilterIcon), {
            class: "mr-0 h-5 w-5 text-gray-400 sm:mr-3",
            "aria-hidden": "true"
          }),
          _createElementVNode("span", _hoisted_1, _toDisplayString(_ctx.$t('core.actions.Filter')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (!isCallTypeRoute.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("label", _hoisted_5, _toDisplayString(_ctx.$t('call.filters.type')), 1),
                _createVNode(_component_UiMultiselect, {
                  name: "filters_type",
                  modelValue: filter.value.type,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filter.value.type) = $event)),
                  "can-clear": "",
                  options: typeFilterOptions,
                  placeholder: _unref(t)('core.options.All')
                }, null, 8, ["modelValue", "placeholder"])
              ]))
            : _createCommentVNode("", true),
          (!isCallStatusRoute.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("label", _hoisted_7, _toDisplayString(_ctx.$t('call.filters.status')), 1),
                _createVNode(_component_UiMultiselect, {
                  name: "filters_status",
                  modelValue: filter.value.status,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filter.value.status) = $event)),
                  "can-clear": "",
                  options: statusFilterOptions,
                  placeholder: _unref(t)('core.options.All')
                }, null, 8, ["modelValue", "placeholder"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, _toDisplayString(_ctx.$t('call.filters.numberClient')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "filters_numberClient",
              modelValue: filter.value.numberClient,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filter.value.numberClient) = $event)),
              label: "maskedNumber",
              "value-prop": "id",
              "can-clear": "",
              placeholder: _ctx.$t('core.multiselect.typeSearch'),
              options: clientNumberOptions.value
            }, null, 8, ["modelValue", "placeholder", "options"])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("label", _hoisted_11, _toDisplayString(_ctx.$t('call.filters.nameAgent')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "filters_user",
              modelValue: filter.value.user,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filter.value.user) = $event)),
              label: "name",
              "value-prop": "id",
              "can-clear": "",
              placeholder: _ctx.$t('core.multiselect.typeSearch'),
              options: agentOptions.value
            }, {
              "option-label": _withCtx(({ option }) => [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("span", null, _toDisplayString(option.name), 1),
                    _createElementVNode("span", {
                      class: _normalizeClass([
                                                option.isActive
                                                    ? 'bg-success-50 text-success-700'
                                                    : 'bg-danger-100 text-danger-700',
                                                'rounded border px-2 py-0.5 text-xs',
                                            ])
                    }, _toDisplayString(option.isActive
                                                    ? _ctx.$t('core.options.Active')
                                                    : _ctx.$t('core.options.Inactive')), 3)
                  ]),
                  _createElementVNode("div", _hoisted_14, _toDisplayString(option.email), 1)
                ])
              ]),
              _: 1
            }, 8, ["modelValue", "placeholder", "options"])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("label", _hoisted_16, _toDisplayString(_ctx.$t('report.call.filters.userLabel')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "filter_userLabel",
              modelValue: filter.value.userLabel,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filter.value.userLabel) = $event)),
              label: "label",
              "value-prop": "id",
              options: userLabelOptions.value,
              "can-clear": "",
              placeholder: _unref(t)('core.options.All')
            }, null, 8, ["modelValue", "options", "placeholder"])
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("label", _hoisted_18, _toDisplayString(_ctx.$t('call.filters.words')), 1),
            _createElementVNode("div", _hoisted_19, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(callParticipantOptions, (option, index) => {
                return _createVNode(_component_UiButton, {
                  key: option.value,
                  id: `participant-button-${index}`,
                  variant: getButtonVariant(option.value),
                  "text-variant": getTextVariant(option.value),
                  size: "md",
                  "no-rounded": "",
                  class: "-ml-px flex-1 first:ml-0 first:rounded-l-md last:rounded-r-md",
                  onClick: ($event: any) => (filterCallParticipantTypes(option.value))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(option.label), 1)
                  ]),
                  _: 2
                }, 1032, ["id", "variant", "text-variant", "onClick"])
              }), 64))
            ]),
            _createVNode(_component_UiMultiselect, {
              class: "mb-4",
              name: "filters_word",
              modelValue: filter.value.word,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filter.value.word) = $event)),
              label: "word",
              "value-prop": "id",
              options: wordsOptions.value,
              "can-clear": "",
              placeholder: 
                                isLoadingWords.value ? _ctx.$t(`core.Loading`) : _unref(t)(`core.options.All`)
                            ,
              loading: isLoadingWords.value
            }, null, 8, ["modelValue", "options", "placeholder", "loading"])
          ]),
          _createElementVNode("div", _hoisted_20, [
            _createElementVNode("label", _hoisted_21, _toDisplayString(_ctx.$t('call.filters.media')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "filters_media",
              modelValue: filter.value.media,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((filter.value.media) = $event)),
              "can-clear": "",
              options: mediaFilterOptions.value,
              placeholder: _unref(t)('core.options.All')
            }, null, 8, ["modelValue", "options", "placeholder"])
          ])
        ]),
        _createElementVNode("div", _hoisted_22, [
          _createVNode(_component_UiButton, {
            variant: "white",
            "text-variant": "gray-700",
            onClick: _cache[8] || (_cache[8] = ($event: any) => (clearFilters()))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Clear')), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_UiButton, {
            variant: "primary",
            "text-variant": "white",
            onClick: _cache[9] || (_cache[9] = ($event: any) => (applyFilters()))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Apply')), 1)
            ]),
            _: 1
          })
        ])
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})